import { Input, Text, Title } from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { invalidateBookabilityStatus } from "@src/appV2/OpenShifts/ShiftAction/api/useBookabilityStatus";
import { useUpdateWorker } from "@src/appV2/Worker/useUpdateWorker";
import { useQueryClient } from "@tanstack/react-query";
import { differenceInYears, isValid, parse } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { BackgroundCheckPageContainer } from "./BackgroundCheckPageContainer";

const DATE_FORMAT = "MM/dd/yyyy";
// Checkr doesn't allow creating a candidate to start a background check with a DoB less than 14 years old.
const MINIMUM_AGE = 14;

function parseDateOfBirth(dateString: string): Date {
  return parse(dateString, DATE_FORMAT, new Date());
}

const backgroundCheckSchema = z
  .object({
    dateOfBirth: z
      .string()
      .min(1, "Date of birth is required")
      // The regex is safe as it think \/ is a useless escape (but it's actually required)
      // eslint-disable-next-line security/detect-unsafe-regex
      .regex(/^(?:\d{2}\/){2}\d{4}$/, `Date must be in ${DATE_FORMAT.toUpperCase()} format`)
      .refine((dateString) => {
        return isValid(parseDateOfBirth(dateString));
      }, "Please enter a valid date")
      .refine((dateString) => {
        return parseDateOfBirth(dateString) <= new Date();
      }, "Date of birth cannot be in the future")
      .refine((dateString) => {
        return differenceInYears(new Date(), parseDateOfBirth(dateString)) >= MINIMUM_AGE;
      }, `You must be at least ${MINIMUM_AGE} years old`),

    socialSecurityNumber: z
      .string()
      .regex(/^\d{3}-\d{2}-\d{4}$/, "SSN must be in format 000-00-0000"),

    confirmSocialSecurityNumber: z.string().min(1, "Please re-enter your Social Security number"),
  })
  .refine((data) => data.socialSecurityNumber === data.confirmSocialSecurityNumber, {
    message: "Social Security numbers must match",
    path: ["confirmSocialSecurityNumber"],
  });

type BackgroundCheckFormData = z.infer<typeof backgroundCheckSchema>;

/**
 * Given a string value of a date of birth, formats it for display with slashes.
 * Example input: 01011990
 * Output: 01/01/1990
 *
 * Example input: 01/01/19
 * Output: 01/01/19
 */
function formatDateOfBirthForView(value: string): string {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length <= 2) {
    return cleaned;
  }

  if (cleaned.length <= 4) {
    return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
  }

  return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
}

/**
 * Parses a date string in MM/DD/YYYY format and returns an ISO-formatted date string (YYYY-MM-DD)
 * Example input: 01/15/1990
 * Output: "1990-01-15"
 */
function parseDateOfBirthForSave(dateString: string): string {
  const [month, day, year] = dateString.split("/").map(Number);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Given a string value of an SSN, returns another string that is composed of just the digits
 * Example input: 123-12-1234
 * Output: 123121234
 */
function formatSSNForSave(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Given a string value of an SSN, returns another string that is formatted for pretty viewing.
 * Example input: 123121234
 * Output: 123-12-1234
 *
 * Example input: 123-12-1
 * Output: 123-12-1
 */
function formatSSNForView(value: string): string {
  const cleaned = formatSSNForSave(value);
  if (cleaned.length <= 3) {
    return cleaned;
  }

  if (cleaned.length <= 5) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  }

  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
}

interface BackgroundCheckDetailsPageProps {
  onContinue: () => void;
  onBack: () => void;
}

export function BackgroundCheckDetailsPage(props: BackgroundCheckDetailsPageProps) {
  const { onContinue, onBack } = props;
  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
  } = useForm<BackgroundCheckFormData>({
    resolver: zodResolver(backgroundCheckSchema),
    mode: "onSubmit",
    defaultValues: {
      dateOfBirth: "",
      socialSecurityNumber: "",
      confirmSocialSecurityNumber: "",
    },
  });
  const { showSuccessToast, showErrorToast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateWorker, isLoading } = useUpdateWorker({
    onSuccess: async () => {
      showSuccessToast("Details updated successfully");
      await invalidateBookabilityStatus(queryClient);
      onContinue();
    },
    onError: (error) => {
      const ssnDigits = getValues("socialSecurityNumber")?.replace(/\D/g, "");
      logError(APP_V2_APP_EVENTS.BACKGROUND_CHECK_FAILED_TO_UPDATE_WORKER_DETAILS, {
        error,
        metadata: {
          dobProvided: Boolean(getValues("dateOfBirth")),
          ssnProvided: Boolean(ssnDigits),
          ssnLast4: ssnDigits ? ssnDigits.slice(-4) : undefined,
          validationErrors: Object.keys(errors ?? {}),
        },
      });
      showErrorToast("Failed to update details. Please try again.");
    },
  });

  async function onSubmit(data: BackgroundCheckFormData) {
    await updateWorker({
      dob: parseDateOfBirthForSave(data.dateOfBirth),
      fullSocialSecurityNumber: formatSSNForSave(data.socialSecurityNumber),
    });
  }

  useLogEffect(APP_V2_APP_EVENTS.BACKGROUND_CHECK_DETAILS_PAGE_VIEWED);

  return (
    <BackgroundCheckPageContainer
      buttonText="Continue"
      isLoading={isLoading}
      navigateBack={onBack}
      onButtonClick={handleSubmit(onSubmit)}
    >
      <Stack spacing={7}>
        <Stack spacing={3}>
          <Title component="h2" variant="h2">
            We need a few details
          </Title>
          <Text variant="h5" color="text.secondary">
            Your information is encrypted and shared securely with Checkr, our trusted screening
            partner.
          </Text>
        </Stack>

        <Stack spacing={7}>
          {/* ---------- Date of Birth ---------- */}
          <Stack spacing={3}>
            <Text semibold variant="h5">
              Date of birth
            </Text>
            <Controller
              name="dateOfBirth"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="MM/DD/YYYY"
                  inputMode="numeric"
                  type="tel"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                  size="large"
                  error={Boolean(errors.dateOfBirth)}
                  onChange={(event) => {
                    field.onChange(formatDateOfBirthForView(event.target.value));
                  }}
                />
              )}
            />
            {errors.dateOfBirth && (
              <Text variant="caption" color="error">
                {errors.dateOfBirth.message}
              </Text>
            )}
          </Stack>
          {/* ---------- SSN ---------- */}
          <Stack spacing={3}>
            <Text semibold variant="h5">
              Social Security number
            </Text>
            <Controller
              name="socialSecurityNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="000-00-0000"
                  inputMode="numeric"
                  type="tel"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                  size="large"
                  error={Boolean(errors.socialSecurityNumber)}
                  onChange={(event) => {
                    field.onChange(formatSSNForView(event.target.value));
                  }}
                />
              )}
            />
            {errors.socialSecurityNumber && (
              <Text variant="caption" color="error">
                {errors.socialSecurityNumber.message}
              </Text>
            )}
          </Stack>

          {/* ---------- Confirm SSN ---------- */}
          <Stack spacing={3}>
            <Text semibold variant="h5">
              Re-enter Social Security number
            </Text>
            <Controller
              name="confirmSocialSecurityNumber"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="000-00-0000"
                  inputMode="numeric"
                  type="tel"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                  size="large"
                  error={Boolean(errors.confirmSocialSecurityNumber)}
                  onChange={(event) => {
                    field.onChange(formatSSNForView(event.target.value));
                  }}
                />
              )}
            />
            {errors.confirmSocialSecurityNumber && (
              <Text variant="caption" color="error">
                {errors.confirmSocialSecurityNumber.message}
              </Text>
            )}
          </Stack>
        </Stack>
      </Stack>
    </BackgroundCheckPageContainer>
  );
}
